/**
 * KinsCircle — Phase 7A Health Monitoring Engine
 *
 * STRICTLY ADDITIVE. LOCAL-ONLY DIAGNOSTICS.
 *
 * Honors STABILITY.md §1, §3, §4 (rollback safety), §10 (uploads),
 * §13 (read-only-first), §14 (no hidden writes).
 *
 * Purpose:
 *   Lightweight, opt-in, in-memory + localStorage telemetry that records
 *   render durations, failed media loads, upload failures, memory feed
 *   latency, route crashes, and playback failures so owners/admins can
 *   reason about real-world reliability without sending any data off
 *   device.
 *
 * Hard guarantees:
 *   - When `healthMonitoring` flag is OFF, every public function early-returns
 *     a safe no-op envelope. Nothing is recorded, nothing is read, nothing
 *     is persisted.
 *   - NEVER calls Supabase. NEVER calls an edge function. NEVER calls fetch.
 *   - NEVER writes to family_graph_*, profiles, board_posts, media,
 *     storage buckets, or any auth surface.
 *   - NEVER throws. All persistence operations are wrapped in try/catch.
 *   - Bounded memory: a hard cap of MAX_METRICS keeps the ring buffer small
 *     so even pathological producers cannot OOM a low-end mobile browser.
 *   - PII-safe: callers must NOT pass user input, captions, emails, names,
 *     or memory contents into `details`. Only stable enum-like strings,
 *     numeric durations, and short identifiers are acceptable.
 *
 * Storage key:  `kinscircle.health.metrics.v1`
 * Version key:  `kinscircle.health.version`  (bumped if shape changes)
 */

import { isHealthMonitoringEnabled } from '@/lib/ai/featureFlag';

// ── Types ──────────────────────────────────────────────────────────────────

export type HealthMetricKind =
  | 'render_duration'
  | 'media_load_failure'
  | 'media_load_success'
  | 'upload_failure'
  | 'upload_success'
  | 'feed_latency'
  | 'route_crash'
  | 'playback_failure'
  | 'playback_success'
  | 'invite_failure'
  | 'invite_success'
  | 'draft_recovered'
  | 'safe_media_retry';

export type HealthMetric = {
  /** Stable kind enum — see HealthMetricKind. */
  kind: HealthMetricKind;
  /** Optional duration in ms (renders, latencies). Must be a finite number. */
  durationMs?: number;
  /** Optional short stable identifier (e.g. route path, media kind). */
  scope?: string;
  /** Optional safe enum-like detail string. NEVER user content. */
  detail?: string;
  /** Wall-clock timestamp (ms since epoch). */
  ts: number;
};

export type HealthEnvelope<T> =
  | { ok: true; value: T }
  | { ok: false; code: 'disabled' | 'unavailable' | 'invalid' };

// ── Storage ────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'kinscircle.health.metrics.v1';
const VERSION_KEY = 'kinscircle.health.version';
const STORAGE_VERSION = '1';
const MAX_METRICS = 250;
/** Drop entries older than this many ms on every read (24h). */
const TTL_MS = 24 * 60 * 60 * 1000;

const safeNow = (): number => {
  try {
    return Date.now();
  } catch {
    return 0;
  }
};

const hasStorage = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return !!window.localStorage;
  } catch {
    return false;
  }
};

const readRaw = (): HealthMetric[] => {
  if (!hasStorage()) return [];
  try {
    const v = window.localStorage.getItem(VERSION_KEY);
    if (v && v !== STORAGE_VERSION) {
      // Migration: drop incompatible older shapes.
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* non-fatal */
      }
      try {
        window.localStorage.setItem(VERSION_KEY, STORAGE_VERSION);
      } catch {
        /* non-fatal */
      }
      return [];
    }
    if (!v) {
      try {
        window.localStorage.setItem(VERSION_KEY, STORAGE_VERSION);
      } catch {
        /* non-fatal */
      }
    }
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const now = safeNow();
    const out: HealthMetric[] = [];
    for (const r of parsed) {
      if (!r || typeof r !== 'object') continue;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const rec = r as any;
      if (typeof rec.kind !== 'string') continue;
      if (typeof rec.ts !== 'number' || !isFinite(rec.ts)) continue;
      if (now > 0 && now - rec.ts > TTL_MS) continue;
      const m: HealthMetric = { kind: rec.kind as HealthMetricKind, ts: rec.ts };
      if (typeof rec.durationMs === 'number' && isFinite(rec.durationMs)) {
        m.durationMs = rec.durationMs;
      }
      if (typeof rec.scope === 'string' && rec.scope.length <= 64) {
        m.scope = rec.scope;
      }
      if (typeof rec.detail === 'string' && rec.detail.length <= 96) {
        m.detail = rec.detail;
      }
      out.push(m);
    }
    return out;
  } catch {
    return [];
  }
};

const writeRaw = (rows: HealthMetric[]): void => {
  if (!hasStorage()) return;
  try {
    const trimmed = rows.length > MAX_METRICS ? rows.slice(rows.length - MAX_METRICS) : rows;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    window.localStorage.setItem(VERSION_KEY, STORAGE_VERSION);
  } catch {
    /* non-fatal — quota or private mode */
  }
};

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Record a single health metric. No-op when the flag is OFF.
 * NEVER pass user content into `detail`. Caller is responsible for safety.
 */
export const recordHealthMetric = (input: {
  kind: HealthMetricKind;
  durationMs?: number;
  scope?: string;
  detail?: string;
}): HealthEnvelope<HealthMetric> => {
  if (!isHealthMonitoringEnabled()) {
    return { ok: false, code: 'disabled' };
  }
  if (!input || typeof input.kind !== 'string') {
    return { ok: false, code: 'invalid' };
  }
  if (!hasStorage()) {
    return { ok: false, code: 'unavailable' };
  }
  const m: HealthMetric = {
    kind: input.kind,
    ts: safeNow(),
  };
  if (typeof input.durationMs === 'number' && isFinite(input.durationMs) && input.durationMs >= 0) {
    m.durationMs = Math.min(input.durationMs, 600_000); // clamp to 10 min
  }
  if (typeof input.scope === 'string' && input.scope.length > 0) {
    m.scope = input.scope.slice(0, 64);
  }
  if (typeof input.detail === 'string' && input.detail.length > 0) {
    m.detail = input.detail.slice(0, 96);
  }
  try {
    const existing = readRaw();
    existing.push(m);
    writeRaw(existing);
    return { ok: true, value: m };
  } catch {
    return { ok: false, code: 'unavailable' };
  }
};

/** Read all metrics still within TTL. Empty array when flag OFF. */
export const readHealthMetrics = (): HealthMetric[] => {
  if (!isHealthMonitoringEnabled()) return [];
  return readRaw();
};

/** Wipe the local ring buffer. Always safe. */
export const clearHealthMetrics = (): HealthEnvelope<true> => {
  if (!hasStorage()) return { ok: false, code: 'unavailable' };
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    return { ok: true, value: true };
  } catch {
    return { ok: false, code: 'unavailable' };
  }
};

export type HealthSnapshot = {
  total: number;
  byKind: Record<string, number>;
  recentFailures: number;
  avgRenderMs: number | null;
  avgFeedLatencyMs: number | null;
  oldest: number | null;
  newest: number | null;
};

/** Aggregate snapshot for the SystemHealth dashboard. */
export const snapshotHealth = (): HealthSnapshot => {
  const empty: HealthSnapshot = {
    total: 0,
    byKind: {},
    recentFailures: 0,
    avgRenderMs: null,
    avgFeedLatencyMs: null,
    oldest: null,
    newest: null,
  };
  if (!isHealthMonitoringEnabled()) return empty;
  const rows = readRaw();
  if (rows.length === 0) return empty;

  const byKind: Record<string, number> = {};
  let renderSum = 0;
  let renderCount = 0;
  let feedSum = 0;
  let feedCount = 0;
  let recentFailures = 0;
  let oldest = rows[0].ts;
  let newest = rows[0].ts;
  const now = safeNow();
  const RECENT_WINDOW = 60 * 60 * 1000; // 1h

  for (const r of rows) {
    byKind[r.kind] = (byKind[r.kind] ?? 0) + 1;
    if (r.ts < oldest) oldest = r.ts;
    if (r.ts > newest) newest = r.ts;
    if (r.kind === 'render_duration' && typeof r.durationMs === 'number') {
      renderSum += r.durationMs;
      renderCount += 1;
    }
    if (r.kind === 'feed_latency' && typeof r.durationMs === 'number') {
      feedSum += r.durationMs;
      feedCount += 1;
    }
    const isFailure =
      r.kind === 'media_load_failure' ||
      r.kind === 'upload_failure' ||
      r.kind === 'route_crash' ||
      r.kind === 'playback_failure' ||
      r.kind === 'invite_failure';
    if (isFailure && now - r.ts <= RECENT_WINDOW) {
      recentFailures += 1;
    }
  }

  return {
    total: rows.length,
    byKind,
    recentFailures,
    avgRenderMs: renderCount > 0 ? Math.round(renderSum / renderCount) : null,
    avgFeedLatencyMs: feedCount > 0 ? Math.round(feedSum / feedCount) : null,
    oldest,
    newest,
  };
};

/**
 * Convenience timer helper. Returns a stop() callback that records the
 * elapsed duration as a `render_duration` metric (or whatever kind the
 * caller specifies). No-op when flag OFF.
 */
export const startHealthTimer = (
  kind: HealthMetricKind = 'render_duration',
  scope?: string,
): (() => void) => {
  if (!isHealthMonitoringEnabled()) return () => {};
  let started = false;
  let startedAt = 0;
  try {
    startedAt = safeNow();
    started = true;
  } catch {
    return () => {};
  }
  return () => {
    if (!started) return;
    const dur = safeNow() - startedAt;
    if (dur < 0 || !isFinite(dur)) return;
    recordHealthMetric({ kind, durationMs: dur, scope });
  };
};
